/**
 * LanguageDetectedToast — first-visit announcement showing the
 * diner that we've automatically loaded the site in their
 * browser/device language.
 *
 * Rationale: i18next-browser-languagedetector already switches
 * the UI; this toast just makes the change DISCOVERABLE so
 * non-English speakers know it's intentional and that English
 * is one click away if they prefer.
 *
 * Triggers only once per browser (localStorage dismissal flag).
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const KEY = "hp_lang_detected_v1";

// Map i18next codes → human English labels (for the prompt that
// non-speakers will universally recognise: "Italiano · Italian").
const HUMAN_NAMES = {
  en: "English",      it: "Italiano",   vi: "Tiếng Việt",
  ar: "العربية",     pa: "ਪੰਜਾਬੀ",     el: "Ελληνικά",
  hi: "हिन्दी",       es: "Español",    fr: "Français",
  zh: "中文",         ja: "日本語",      ko: "한국어",
  de: "Deutsch",      pt: "Português",  pl: "Polski",
  ru: "Русский",      tr: "Türkçe",     th: "ไทย",
  id: "Bahasa",       nl: "Nederlands", sv: "Svenska",
};

export default function LanguageDetectedToast() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  // Banner: detected non-English browser language on first visit
  useEffect(() => {
    try {
      if (localStorage.getItem(KEY)) return;
    } catch { /* private mode */ }

    const detected = (i18n.language || "en").split("-")[0];
    if (detected === "en") {
      // Even English visitors get the flag set so the toast
      // never fires twice. No banner needed.
      try { localStorage.setItem(KEY, "1"); } catch { /* */ }
      return;
    }

    const native = HUMAN_NAMES[detected] || detected.toUpperCase();
    toast(`🌐 Site auto-set to ${native}`, {
      description: "Tap to switch back to English",
      duration:  8000,
      action: {
        label: "English",
        onClick: () => {
          i18n.changeLanguage("en");
          try { localStorage.setItem(KEY, "1"); } catch { /* */ }
        },
      },
      onDismiss: () => {
        try { localStorage.setItem(KEY, "1"); } catch { /* */ }
      },
      onAutoClose: () => {
        try { localStorage.setItem(KEY, "1"); } catch { /* */ }
      },
    });
  }, [i18n]);

  // Sync language ↔ user profile: signed-in customers store
  // their preference server-side so it travels across devices.
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        if (cancelled) return;
        const saved = data?.locale;
        const current = (i18n.language || "en").split("-")[0];
        if (saved && saved !== current) {
          // Profile-saved locale overrides browser detection
          i18n.changeLanguage(saved);
        } else if (!saved && current) {
          // Persist what we auto-detected so other devices match
          try {
            await api.patch("/me/profile", { locale: current });
          } catch { /* non-fatal */ }
        }
      } catch { /* offline / non-customer */ }
    })();
    return () => { cancelled = true; };
  }, [user, i18n]);

  // Save manual switches back to the user profile
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    const onChange = async (code) => {
      const base = (code || "").split("-")[0];
      try {
        await api.patch("/me/profile", { locale: base });
      } catch { /* offline */ }
    };
    i18n.on("languageChanged", onChange);
    return () => { i18n.off("languageChanged", onChange); };
  }, [user, i18n]);

  return null;
}
